import React, { useEffect, useMemo, useState } from "react";
import TableContainer from "components/Common/TableContainer";
import Breadcrumbs from "components/Common/Breadcrumb";
import SeatsModal from "./SeatsModal";

import {
    getExamRoomAssignments    as onGetExamRoomAssignments,
    generateExamRoomSeats     as onGenerateSeats,
    getExamRoomSeats          as onGetSeats,
    resetExamRoomSeats        as onResetSeats,
} from "store/actions";

import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { Col, Row, Card, CardBody, Button, Badge } from "reactstrap";
import Spinners from "components/Common/Spinner";
import { ToastContainer, toast } from "react-toastify";

const AssignmentsPage = () => {
    document.title = "Programmation des salles | CinzanEdu";

    const dispatch = useDispatch();

    const [seatsModal,    setSeatsModal]    = useState(false);
    const [selectedExamRoomAssignment, setSelectedExamRoomAssignment] = useState(null);

    const Properties = createSelector(
        (state) => state.examRoomAssignmentReducer,
        (r) => ({
            examRoomAssignments:    r.examRoomAssignments    || [],
            seats:          r.seats          || [],
            seatsStats:     r.seatsStats     || null,
            isGenerating:   r.isGenerating   || false,
            loading:        r.loading,
        })
    );

    const { examRoomAssignments, seats, seatsStats, isGenerating, loading} = useSelector(Properties);

    const [isLoading, setLoading] = useState(true);



    useEffect(() => {
        dispatch(onGetExamRoomAssignments());
    }, [dispatch]);

    // Toast après génération réussie
    useEffect(() => {
        if (seatsStats && !isGenerating) {
            toast.success(`${seatsStats.total_seats} places générées avec succès !`);
        }
    }, [seatsStats, isGenerating]);

    const handleGenerateSeats = (examRoomAssignment) => {
        dispatch(onGenerateSeats(examRoomAssignment.uuid));
    };

    const handleViewSeats = (examRoomAssignment) => {
        setSelectedExamRoomAssignment(examRoomAssignment);
        dispatch(onGetSeats(examRoomAssignment.uuid));
        setSeatsModal(true);
    };

    const handleResetSeats = (examRoomAssignment) => {
        if (window.confirm("Voulez-vous vraiment réinitialiser les places ?")) {
            dispatch(onResetSeats(examRoomAssignment.uuid));
        }
    };

    const statusBadge = (status) => {
        const map = {
            planifie: { color: "warning",  label: "Planifié"  },
            en_cours: { color: "primary",  label: "En cours"  },
            termine:  { color: "success",  label: "Terminé"   },
        };
        const s = map[status] || { color: "secondary", label: status };
        return <Badge color={s.color}>{s.label}</Badge>;
    };

    const columns = useMemo(() => [
        {
            header: "Salle",
            accessorKey: "exam_room.name",
            enableColumnFilter: false,
            enableSorting: true,
            cell: (cellProps) => (
                <div>
                    <div className="fw-bold">
                        {cellProps.row.original.exam_room?.name}
                    </div>
                    <small className="text-muted">
                        {cellProps.row.original.exam_room?.capacity} places
                    </small>
                </div>
            ),
        },
        {
            header: "Évaluation",
            accessorKey: "evaluation.title",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Date",
            accessorKey: "date",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Horaire",
            accessorKey: "start_time",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cellProps) => (
                <span>
                    {cellProps.row.original.start_time} →{" "}
                    {cellProps.row.original.end_time}
                </span>
            ),
        },
        {
            header: "Classes",
            accessorKey: "class_configs",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cellProps) => {
                const configs = cellProps.row.original.class_configs || [];
                return (
                    <div className="d-flex flex-wrap gap-1">
                        {configs.map((c, i) => (
                            <Badge key={i} color="info" className="fw-normal">
                                {c.study_level?.name} ({c.alphabet_start}→{c.alphabet_end}, max {c.max_students})
                            </Badge>
                        ))}
                    </div>
                );
            },
        },
        {
            header: "Statut",
            accessorKey: "status",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cellProps) => statusBadge(cellProps.row.original.status),
        },
        {
            header: "Actions",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cellProps) => {

                console.log("ASSI: ", cellProps.row.original);
                const a           = cellProps.row.original;
                const hasSeats    = a.seats_count > 0;

                return (
                    <ul className="list-unstyled hstack gap-1 mb-0">
                        {/* Générer les places */}
                        {!hasSeats ? (
                            <li>
                                <Button
                                    color="success"
                                    size="sm"
                                    onClick={() => handleGenerateSeats(a)}
                                    disabled={isGenerating}
                                >
                                    <i className="bx bx-shuffle me-1"></i>
                                    {isGenerating ? "Génération..." : "Générer places"}
                                </Button>
                            </li>
                        ) : (
                            <>
                                {/* Voir les places */}
                                <li>
                                    <Button
                                        color="primary"
                                        size="sm"
                                        onClick={() => handleViewSeats(a)}
                                    >
                                        <i className="bx bx-list-ul me-1"></i>
                                        Voir places
                                    </Button>
                                </li>
                                {/* Réinitialiser */}
                                <li>
                                    <Button
                                        color="danger"
                                        size="sm"
                                        outline
                                        onClick={() => handleResetSeats(a)}
                                    >
                                        <i className="bx bx-reset"></i>
                                    </Button>
                                </li>
                            </>
                        )}
                    </ul>
                );
            },
        },
    ], [isGenerating]);

    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <Breadcrumbs
                        title="Salles d'examen"
                        breadcrumbItem="Programmations"
                    />

                    {loading ? <Spinners setLoading={setLoading} /> :
                        <Row>
                            <Col lg="12">
                                <Card>
                                    <CardBody className="border-bottom d-flex justify-content-between align-items-center">
                                        <div className="text-muted small">
                                            {examRoomAssignments.length} programmation(s)
                                        </div>
                                    </CardBody>
                                    <CardBody>
                                        <TableContainer
                                            columns={columns}
                                            data={examRoomAssignments}
                                            isCustomPageSize={true}
                                            isGlobalFilter={true}
                                            isPagination={true}
                                            SearchPlaceholder="Rechercher..."
                                            tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline"
                                            theadClass="table-light"
                                            pagination="pagination"
                                            paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                                        />
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    }
                </div>
            </div>

            {/* Modal places */}
            <SeatsModal
                isOpen={seatsModal}
                toggle={() => setSeatsModal(false)}
                assignment={selectedExamRoomAssignment}
                seats={seats}
                seatsStats={seatsStats}
            />

            <ToastContainer />
        </React.Fragment>
    );
};

export default AssignmentsPage;